// src/features/stock-info/components/EarningsChart.tsx
//
// Component Island: Earnings Actual vs Estimated. Fetches the last 5 reporting
// periods from GET /stock-info/{symbol}/earnings (backed by FMP /stable/earnings)
// and plots grouped comparison bars. A toggle lets the user switch between:
//   - EPS: epsActual vs epsEstimated
//   - Revenue: revenueActual vs revenueEstimated
//
// FMP returns newest-first; Recharts reads left-to-right, so we reverse() a
// shallow copy before charting.

import { useEffect, useMemo, useState } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { fetchEarnings, type EarningsRow } from '../api';
import {
    formatMoney,
    formatCompactMoney,
    currencyGlyph,
    type CurrencyCode,
} from '../../../shared/utils/currency';

type Metric = 'eps' | 'revenue';

const METRICS: { label: string; value: Metric }[] = [
    { label: 'EPS', value: 'eps' },
    { label: 'Revenue', value: 'revenue' },
];

const ACTUAL_COLOR = '#3b82f6';
const ESTIMATED_COLOR = '#94a3b8';

export const EarningsChart = ({
    symbol,
    currency = 'USD',
}: {
    symbol: string;
    currency?: CurrencyCode;
}) => {
    const glyph = currencyGlyph({ explicitCurrency: currency });
    const formatEps = (val: number) =>
        formatMoney(val, { explicitCurrency: currency });
    const formatRevenue = (val: number) =>
        formatCompactMoney(val, { explicitCurrency: currency });
    const [metric, setMetric] = useState<Metric>('eps');
    const [data, setData] = useState<EarningsRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchEarnings(symbol, 5)
            .then((res) => {
                // Reverse so Recharts plots oldest -> newest (left to right).
                if (!cancelled) setData([...(res ?? [])].reverse());
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                if (!cancelled) {
                    setError('Earnings data unavailable.');
                    setData([]);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol]);

    const isEps = metric === 'eps';
    const formatValue = isEps ? formatEps : formatRevenue;

    const chartData = useMemo(
        () =>
            data.map((row) => ({
                date: row.date ?? '—',
                actual: isEps ? row.eps_actual : row.revenue_actual,
                estimated: isEps ? row.eps_estimated : row.revenue_estimated,
            })),
        [data, isEps],
    );

    const hasData = chartData.some(
        (d) => d.actual != null || d.estimated != null,
    );

    return (
        <div className="surface-card border-t-4 border-blue-500 flex flex-col h-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                <h2 className="section-title mb-0">Earnings: Actual vs Estimated</h2>

                <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1 border border-slate-200 dark:border-slate-700">
                    {METRICS.map((m) => (
                        <button
                            key={m.value}
                            onClick={() => setMetric(m.value)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                                metric === m.value
                                    ? 'bg-blue-600 text-white shadow'
                                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                            }`}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="relative h-[300px] w-full">
                {isLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
                        <span className="text-blue-500 font-medium animate-pulse">
                            Loading Earnings…
                        </span>
                    </div>
                )}

                {!isLoading && error && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                        {error}
                    </div>
                )}

                {!isLoading && !error && !hasData && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                        No earnings data available.
                    </div>
                )}

                {!isLoading && !error && hasData && (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={chartData}
                            margin={{ left: -8, top: 16, right: 0, bottom: 0 }}
                            barGap={4}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="#334155"
                                opacity={0.3}
                            />
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                tickFormatter={(val: number) =>
                                    isEps
                                        ? `${glyph}${val.toFixed(2)}`
                                        : val.toLocaleString(undefined, {
                                              notation: 'compact',
                                          })
                                }
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                                width={70}
                            />
                            <Tooltip
                                cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }}
                                contentStyle={{
                                    backgroundColor: '#0f172a',
                                    borderColor: '#334155',
                                    borderRadius: '8px',
                                    color: '#fff',
                                }}
                                labelStyle={{ color: '#94a3b8', marginBottom: '4px' }}
                                formatter={(value: number | string, name: string) => [
                                    typeof value === 'number' ? formatValue(value) : 'N/A',
                                    name,
                                ]}
                            />
                            <Legend
                                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                                iconType="circle"
                            />
                            <Bar
                                dataKey="estimated"
                                name="Estimated"
                                fill={ESTIMATED_COLOR}
                                radius={[4, 4, 0, 0]}
                                isAnimationActive={false}
                            />
                            <Bar
                                dataKey="actual"
                                name="Actual"
                                fill={ACTUAL_COLOR}
                                radius={[4, 4, 0, 0]}
                                isAnimationActive={false}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

export default EarningsChart;
